import { type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import {
  TABLE_GRID_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { useUploadsTableController } from '@/features/uploads/components/useUploadsTableController';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { resolveDeleteUploadDialogCopy } from './UploadsTable.dialogCopy';
import type { UploadsTableProps } from '@/features/uploads/components/UploadsTable.types';
import type { UploadDto } from '@flowqa/shared';

export const UploadsTable = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions,
  initialPageSize,
  disablePageSizeSelect,
  search,
  noRowsText,
  fixedFilters,
  queryFn,
  queryKey,
  initialData,
  showLastUpdated = false,
  onRowsChange,
  onQueryMeta,
  initialSortModel,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  disableColumnSorting = true,
  disableColumnMenu = true,
  tagOptionsOverride,
  refetchOnMount,
  preservePreviousData = false,
  showSkeletonOnFetch = true,
  showPaginationSkeletonWhenRowsVisible = true,
  dynamicRowHeight = true,
  active = true,
}: UploadsTableProps): ReactElement => {
  const {
    columns,
    deleteDialogPurpose,
    deleteLoading,
    deleteTarget,
    editTarget,
    editTargetName,
    externalLoading,
    gridConfig,
    gridProps,
    handleCloseEditDialog,
    handleSaveEditDialog,
    isAuthenticated,
    onDeleteClose,
    onDeleteConfirm,
    projectId,
    queryPath,
    refetchIntervalMs,
    selectedOrganisationId,
    togglableColumnFields,
    updateRowTagsColorPendingId,
  } = useUploadsTableController({
    disablePageSizeSelect,
    fixedFilters,
    initialPageSize,
    initialSortModel,
    noRowsText,
    onRowsChange,
    pageSizeOptions,
    persistKey,
    queryFn,
    queryKey,
    showLastUpdated,
    syncStateToUrl,
    tagOptionsOverride,
  });
  const deleteDialogCopy = resolveDeleteUploadDialogCopy(deleteDialogPurpose);

  return (
    <>
      <ServerDataTable<UploadDto>
        queryPath={queryPath}
        {...gridConfig}
        {...gridProps}
        initialData={initialData}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={isAuthenticated}
        externalLoading={externalLoading}
        organisationId={selectedOrganisationId}
        projectId={projectId}
        search={search}
        onQueryMeta={onQueryMeta}
        columnVisibilityModel={columnVisibilityModel}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        interactionMode="static"
        getRowHeight={dynamicRowHeight ? () => 'auto' : undefined}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={dynamicRowHeight ? null : undefined}
        refetchOnMount={refetchOnMount}
        preservePreviousData={preservePreviousData}
        showSkeletonOnFetch={showSkeletonOnFetch}
        showPaginationSkeletonWhenRowsVisible={showPaginationSkeletonWhenRowsVisible}
        active={active}
        sx={[
          ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
          {
            '& .MuiDataGrid-cell[data-field="fileName"], & .MuiDataGrid-cell[data-field="status"], & .MuiDataGrid-cell[data-field="id"], & .MuiDataGrid-cell[data-field="error"], & .MuiDataGrid-cell[data-field="actions"]':
              {
                alignItems: 'flex-start',
              },
            '& .MuiDataGrid-columnHeaders': {
              minHeight: `${TABLE_GRID_HEADER_HEIGHT}px !important`,
            },
          },
        ]}
        autoHeight
        refetchIntervalMs={refetchIntervalMs}
        gridProps={{
          getEstimatedRowHeight: dynamicRowHeight ? () => TABLE_ROW_MEASURE_HEIGHT : undefined,
          rowHeight: dynamicRowHeight ? undefined : TABLE_ROW_HEIGHT,
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Upload"
        entityName={editTargetName}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        loading={updateRowTagsColorPendingId === (editTarget?.id ?? null)}
        cascadeCopy="Tags will be applied to all associated results files, execution runs, suite runs and test runs."
        onClose={handleCloseEditDialog}
        onSave={handleSaveEditDialog}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={deleteDialogCopy.title}
        description={deleteDialogCopy.description}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'upload' })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
    </>
  );
};
